/**
 * Price History Utility (web)
 *
 * Records every price change to the price_history table keyed by
 * inventory_item_id — NOT the item name — so history survives renames.
 *
 * Callers: UpdatePrices, InventoryManager, CountInterface.
 * Mobile equivalent: apps/mobile/src/features/counting/nativeCountData.js
 *
 * Sources:
 *   'ai_price_import'  — UpdatePrices.jsx AI invoice matching tool
 *   'item_editor'      — InventoryManager.jsx item detail editor
 *   'web_count'        — CountInterface.jsx count session item editor
 *   'ci_chat'          — Counter Intelligence natural-language chat actions
 */

import { SUPABASE_URL, SUPABASE_KEY } from '../config/database';

async function getAuthHeaders() {
  // Try the global supabase client's live session first
  try {
    if (window.supabase?.auth) {
      const { data } = await window.supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (token) return { Authorization: `Bearer ${token}`, apikey: SUPABASE_KEY, 'Content-Type': 'application/json' };
    }
  } catch (_) {}
  // Fallback: scan localStorage for sb-*-auth-token (same as UpdatePrices / FileUpload)
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith('sb-') || !key.endsWith('-auth-token')) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (parsed?.access_token) return { Authorization: `Bearer ${parsed.access_token}`, apikey: SUPABASE_KEY, 'Content-Type': 'application/json' };
    } catch (_) {}
  }
  // Last resort: anon key (changed_by will be null)
  return { Authorization: `Bearer ${SUPABASE_KEY}`, apikey: SUPABASE_KEY, 'Content-Type': 'application/json' };
}

/**
 * @param {object} params
 * @param {number}  params.inventoryItemId
 * @param {number}  params.storeId
 * @param {string}  params.oldName
 * @param {string}  params.newName
 * @param {number|null} params.oldCasePrice
 * @param {number|null} params.newCasePrice
 * @param {number|null} params.oldUnitPrice
 * @param {number|null} params.newUnitPrice
 * @param {'ai_price_import'|'item_editor'|'web_count'|'import_approval'|'ci_chat'} params.source
 * @param {string}  [params.invoiceVendor]
 * @param {string}  [params.invoiceUnit]
 * @param {number|null} [params.priceDeltaAbsolute]  – pre-computed absolute delta (new - old)
 * @param {number|null} [params.priceDeltaPct]       – pre-computed % delta
 * @param {string|null} [params.invoiceDate]   – YYYY-MM-DD the invoice is dated; dates the price event (changed_at)
 * @param {string|null} [params.invoiceNumber] – invoice reference, stamped on the row for reporting
 * @param {string|null} [params.importDate]    – ISO timestamp the invoice was imported; recorded, not used for pricing
 * @param {string|null} [params.changedAt]     – explicit effective date/time (YYYY-MM-DD or ISO timestamp); overrides invoiceDate
 */
function computeChangeType(oldPrice, newPrice) {
  const o = toNum(oldPrice);
  const n = toNum(newPrice);
  if ((o == null || o === 0) && n != null && n > 0) return 'setup';
  if (o != null && o > 0 && n != null && n > 0) return 'updated';
  if (o != null && o > 0 && (n == null || n === 0)) return 'removed';
  return null;
}

/**
 * Invoice-sourced price events are dated by the invoice date, stored at noon UTC
 * so the calendar day survives a re-render in a local timezone (a midnight-UTC
 * stamp would slip to the day before in the Americas). Returns null for a
 * missing / invalid date — the caller then lets changed_at default to now().
 */
function invoiceDateToChangedAt(invoiceDate) {
  const s = String(invoiceDate ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map((p) => parseInt(p, 10));
  if (y < 2000 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${s}T12:00:00.000Z`;
}

/**
 * Accept an explicit effective date from a caller (e.g. CI chat). A bare date
 * string is anchored to noon UTC so the calendar day survives timezone rendering;
 * a full ISO timestamp is passed through unchanged.
 */
function toChangedAtTimestamp(value) {
  if (!value) return null;
  const s = String(value).trim();
  // ISO timestamp with time component — pass through.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/.test(s)) return s;
  // Bare date — noon UTC.
  return invoiceDateToChangedAt(s);
}

export async function recordPriceHistory({
  inventoryItemId,
  storeId,
  oldName = null,
  newName = null,
  oldCasePrice = null,
  newCasePrice = null,
  oldUnitPrice = null,
  newUnitPrice = null,
  source,
  invoiceVendor = null,
  invoiceUnit = null,
  priceDeltaAbsolute = null,
  priceDeltaPct = null,
  invoiceDate = null,
  invoiceNumber = null,
  importDate = null,
  changedAt = null,
  // Multi-vendor linkage (migration 20260724120005): which vendor product the
  // price came from, and the resulting cost per count unit. Without these a
  // price event cannot be attributed to one of an item's several vendors.
  purchaseOptionId = null,
  normalizedUnitCost = null,
  effectiveDate = null,
}) {
  // Skip if nothing actually changed
  const caseChanged = toNum(oldCasePrice) !== toNum(newCasePrice);
  const unitChanged = toNum(oldUnitPrice) !== toNum(newUnitPrice);
  if (!caseChanged && !unitChanged) return;

  const headers = await getAuthHeaders();
  // Extract user id from the JWT we got
  let changedBy = null;
  try {
    const token = headers.Authorization?.replace('Bearer ', '');
    if (token && token !== SUPABASE_KEY) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      changedBy = payload.sub ?? null;
    }
  } catch (_) {}

  const computedDeltaAbsolute = priceDeltaAbsolute ?? (() => {
    const n = toNum(newCasePrice);
    const o = toNum(oldCasePrice);
    return (n != null && o != null) ? n - o : null;
  })();
  const computedDeltaPct = priceDeltaPct ?? (() => {
    const o = toNum(oldCasePrice);
    if (!computedDeltaAbsolute || !o) return null;
    return (computedDeltaAbsolute / o) * 100;
  })();

  // Invoice imports date the price event by the invoice date, and keep the
  // actual import time separately (import_date). When the invoice carried no
  // readable date, fall back to the import date so changed_at still reflects
  // when we learned the price. Non-invoice callers pass neither invoiceDate nor
  // importDate, so changed_at stays unset and defaults to now() at the database.
  // An explicit changedAt (from CI chat, for example) takes precedence over
  // invoice-derived dates.
  const effectiveChangedAt = toChangedAtTimestamp(changedAt) || invoiceDateToChangedAt(invoiceDate) || importDate || null;

  const record = {
    inventory_item_id: inventoryItemId,
    store_id: storeId,
    old_name: oldName ?? null,
    new_name: newName ?? null,
    old_case_price: toNum(oldCasePrice),
    new_case_price: toNum(newCasePrice),
    old_unit_price: toNum(oldUnitPrice),
    new_unit_price: toNum(newUnitPrice),
    changed_by: changedBy,
    source,
    invoice_vendor: invoiceVendor ?? null,
    invoice_unit: invoiceUnit ?? null,
    price_delta_absolute: computedDeltaAbsolute ?? null,
    price_delta_pct: computedDeltaPct ?? null,
    change_type: computeChangeType(oldCasePrice, newCasePrice),
    invoice_number: invoiceNumber ?? null,
    import_date: importDate ?? null,
    ...(effectiveChangedAt ? { changed_at: effectiveChangedAt } : {}),
  };

  // Kept separate so a database without migration 20260724120005 can be
  // retried without them rather than losing the price event entirely.
  const optionLinkage = {
    ...(purchaseOptionId ? { purchase_option_id: purchaseOptionId } : {}),
    ...(toNum(normalizedUnitCost) !== null ? { normalized_unit_cost: toNum(normalizedUnitCost) } : {}),
    ...(effectiveDate ? { effective_date: effectiveDate } : {}),
  };

  const post = (body) => fetch(`${SUPABASE_URL}/rest/v1/price_history`, {
    method: 'POST',
    headers: { ...headers, Prefer: 'return=minimal' },
    body: JSON.stringify(body),
  });

  try {
    let resp = await post({ ...record, ...optionLinkage });
    if (!resp.ok && Object.keys(optionLinkage).length > 0) {
      const txt = await resp.text().catch(() => '');
      if (/column .* does not exist|PGRST204/i.test(txt)) {
        console.warn('[price-history] Option linkage columns unavailable; recording without them.');
        resp = await post(record);
      } else {
        console.warn('[price-history] Insert failed:', resp.status, txt);
        return;
      }
    }
    if (!resp.ok) {
      const txt = await resp.text().catch(() => '');
      console.warn('[price-history] Insert failed:', resp.status, txt);
    }
  } catch (err) {
    // Non-fatal — never block the price update itself
    console.warn('[price-history] Error recording change:', err?.message);
  }
}

function toNum(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

